import { useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { VinUploadButtons } from "./VinUploadButtons";
import { processVinImage } from "@/utils/imageProcessing";

interface VinImageUploadProps {
  onVinDetected: (vin: string) => void;
  isLoading: boolean;
}

export const VinImageUpload = ({ onVinDetected, isLoading }: VinImageUploadProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState(false);

  const handleImageProcess = async (file: File) => {
    console.log("Starting image processing...");
    setProcessing(true);
    
    try {
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file (JPEG, PNG, etc.)');
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        throw new Error('File size too large. Please upload an image smaller than 10MB.');
      }

      const result = await processVinImage(file);
      
      if (result.vin) {
        console.log("VIN detected:", result.vin);
        onVinDetected(result.vin);
        toast({
          title: "Success",
          description: "VIN detected successfully",
        });
      } else {
        console.error("VIN detection failed:", result.error);
        toast({
          title: "VIN Detection Failed",
          description: result.error || "No VIN found in image. Please ensure the VIN is clearly visible and try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error in handleImageProcess:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log("File input change event triggered");
    const file = event.target.files?.[0];
    
    if (!file) {
      console.log("No file selected - user likely cancelled");
      return;
    }

    console.log("File selected:", file.name, file.type, file.size);
    await handleImageProcess(file);
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleTakePhoto = () => {
    console.log("Take photo button clicked");
    if (fileInputRef.current) {
      fileInputRef.current.accept = "image/*;capture=camera";
      fileInputRef.current.click();
    }
  };

  const handleUploadImage = () => {
    console.log("Upload image button clicked");
    if (fileInputRef.current) {
      fileInputRef.current.accept = "image/*";
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-4">
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        ref={fileInputRef}
        disabled={processing || isLoading}
      />
      
      <VinUploadButtons
        processing={processing}
        isLoading={isLoading}
        onTakePhoto={handleTakePhoto}
        onUploadImage={handleUploadImage}
      />
      
      <div className="text-sm text-muted-foreground text-center">
        Upload a clear image of the VIN number or take a photo
      </div>
    </div>
  );
};